package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.TeamJoin
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.growth.GetReferralLinkUseCase
import com.evoapps.evoshift.domain.usecase.growth.ObserveTeamJoinUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.settings.LocalSettings
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * SH-239: the one-time "Recommend EVoShift" card after someone joins a team through an invitation,
 * kept on this device per person: pending from the join until it is opened or dismissed.
 */
class RecommendPromptStore(private val settings: LocalSettings) {
    fun markPending(uid: String) = put(uid, PENDING)

    fun isPending(uid: String): Boolean = runCatching { settings.getString(KEY_PREFIX + uid) == PENDING }.getOrDefault(false)

    fun done(uid: String) = put(uid, DONE)

    private fun put(uid: String, value: String) {
        runCatching { settings.putString(KEY_PREFIX + uid, value) }
    }

    companion object {
        const val KEY_PREFIX = "recommend_prompt_"
        const val PENDING = "pending"
        const val DONE = "done"
    }
}

/**
 * SH-239: "Recommend EVoShift to another employer": [link] is the person's referral link (made once,
 * a random code, nothing else recorded); [isLoading] only until it is known, [error] when it could
 * not be read or made (offline on the first use).
 */
data class RecommendUiState(
    val isLoading: Boolean = true,
    val link: String? = null,
    val error: UiText? = null
)

class RecommendViewModel(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getReferralLink: GetReferralLinkUseCase,
    private val prompt: RecommendPromptStore
) : ViewModel() {
    private val _state = MutableStateFlow(RecommendUiState())
    val state = _state.asStateFlow()

    init {
        load()
    }

    fun retry() = load()

    private fun load() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = it.link == null, error = null) }
            val uid = getCurrentUser().firstOrNull()?.uid
            if (uid == null) {
                _state.update { it.copy(isLoading = false) }
                return@launch
            }
            // Opened: the card after joining has done its job.
            prompt.done(uid)
            getReferralLink(uid).fold(
                onSuccess = { link -> _state.update { it.copy(isLoading = false, link = link, error = null) } },
                onFailure = { e -> _state.update { it.copy(isLoading = false, error = e.toUiText()) } }
            )
        }
    }
}

/** SH-239: the card after joining a team; [shown] until the person opens "Recommend" or dismisses it. */
data class RecommendPromptUiState(val shown: Boolean = false)

class RecommendPromptViewModel(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val prompt: RecommendPromptStore
) : ViewModel() {
    private val _state = MutableStateFlow(RecommendPromptUiState())
    val state = _state.asStateFlow()
    private var uid: String? = null

    init {
        viewModelScope.launch {
            getCurrentUser().map { it?.uid }.distinctUntilChanged().collect { id ->
                uid = id
                _state.value = RecommendPromptUiState(shown = id != null && prompt.isPending(id))
            }
        }
    }

    /** "Not now" or "Recommend": the card goes for good on this device. */
    fun dismiss() {
        uid?.let(prompt::done)
        _state.value = RecommendPromptUiState(shown = false)
    }
}

/**
 * SH-239: the owner's "Invite the team" card at the current workplace: [join] while it is
 * prompted (a month published, most of the team not in the app yet) and not dismissed there.
 */
data class TeamInviteUiState(
    val workplaceId: String? = null,
    val join: TeamJoin? = null
)

@OptIn(ExperimentalCoroutinesApi::class)
class TeamInviteViewModel(
    getCurrentUser: GetCurrentUserUseCase,
    sessionManager: WorkplaceSessionManager,
    private val observeTeamJoin: ObserveTeamJoinUseCase,
    private val settings: LocalSettings,
    plans: ObservePlanActiveUseCase? = null
) : ViewModel() {
    private val _state = MutableStateFlow(TeamInviteUiState())
    val state = _state.asStateFlow()
    private val dismissed = MutableStateFlow<Set<String>>(emptySet())

    init {
        viewModelScope.launch {
            val user = getCurrentUser()
            sessionManager.currentWorkplaceId.flatMapLatest { workplaceId ->
                if (workplaceId == null) flowOf(null to emptySet())
                else capabilitiesAt(user, plans, workplaceId).map { workplaceId to it }
            }.distinctUntilChanged().flatMapLatest { (workplaceId, caps) ->
                val owner = workplaceId != null && Capability.BILLING in caps && Capability.MAKE_CHANGES in caps
                if (!owner) {
                    flowOf(TeamInviteUiState(workplaceId))
                } else {
                    combine(observeTeamJoin(workplaceId!!).catch { }, dismissed) { join, hidden ->
                        val shown = join.invitePrompted && workplaceId !in hidden && !isDismissed(workplaceId)
                        TeamInviteUiState(workplaceId, join.takeIf { shown })
                    }
                }
            }.collect { _state.value = it }
        }
    }

    /** "Hide": the card goes for good at this workplace (on this device). */
    fun dismiss() {
        val workplaceId = _state.value.workplaceId ?: return
        runCatching { settings.putString(DISMISSED_PREFIX + workplaceId, "true") }
        dismissed.update { it + workplaceId }
    }

    private fun isDismissed(workplaceId: String) =
        runCatching { settings.getString(DISMISSED_PREFIX + workplaceId) == "true" }.getOrDefault(false)

    companion object {
        const val DISMISSED_PREFIX = "team_invite_prompt_dismissed_"
    }
}
